import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';
import {
  EvaluationRule as PapEvaluationRule,
  MultipleChildResult,
  Tag as PapTag,
  CategoryResult as PapCategoryResult,
} from '@isophi/pap-api';
import {
  ChildResultCache,
  Tag as MngTag,
  EvaluationRule as MngEvaluationRule,
  CategoryResultCache as MngCategoryResult,
} from '@isophi/mng-api';

import { combineLatest, map, Observable, takeUntil } from 'rxjs';

import { BaseComponent } from '../base-component/base.component';

export interface CategoryResultGroup {
  category: PapTag | MngTag;
  evaluation: {
    code: number | undefined | null;
    backgroundColor: string | undefined | null;
    title: string | undefined | null;
    textColor: string | undefined | null;
  };
  iconUrl: string;
  score: number | null;
  maxScore: number | null;
  percent: number | null;
}

export interface SuperCategoryResultGroup {
  category: PapTag | MngTag;
  iconUrl: string;
  evaluation: { backgroundColor: string } | undefined;
  score: number | null;
  maxScore: number | null;
  percent: number | null;
  categoryResultGroups: CategoryResultGroup[];
}

// Areas of the MŠMT framework have no skill level (only competencies do), so they get a neutral
// color that cannot be mistaken for a level color of the legend (the green #C4E054 is "rozvinutá").
const AREA_EVALUATION = { backgroundColor: '#74797F' };

export interface CategoryHierarchy {
  superCategory: PapTag | MngTag;
  subCategories: (PapTag | MngTag)[];
}

@Component({
  selector: 'isophi-app-bar-chart',
  templateUrl: './bar-chart.component.html',
  styleUrls: ['./bar-chart.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class BarChartComponent extends BaseComponent implements OnInit {
  @Input() result$!: Observable<MultipleChildResult | ChildResultCache>;
  @Input() getCategoryMap!: () => Observable<{ [uuid: string]: PapTag | MngTag }>;
  @Input() getCategoryHierarchy!: () => Observable<CategoryHierarchy[]>;
  @Input() fetchEvaluationRule!: (
    result$: Observable<MultipleChildResult | ChildResultCache>
  ) => Observable<PapEvaluationRule | MngEvaluationRule>;
  @Input() resultName: string | null = null;

  protected categoryMap$!: Observable<{ [uuid: string]: PapTag | MngTag }>;
  protected categoryHierarchy$!: Observable<CategoryHierarchy[]>;
  protected superCategoryResultGroups$!: Observable<SuperCategoryResultGroup[]>;
  protected evaluationRule$!: Observable<PapEvaluationRule | MngEvaluationRule>;

  protected resultDate: Date | null = null;

  ngOnInit(): void {
    this.categoryMap$ = this.getCategoryMap();
    this.categoryHierarchy$ = this.getCategoryHierarchy();
    this.evaluationRule$ = this.fetchEvaluationRule(this.result$);
    this.superCategoryResultGroups$ = this.superCategoryResultGroups();
  }

  private superCategoryResultGroups(): Observable<SuperCategoryResultGroup[]> {
    return combineLatest([this.result$, this.categoryMap$, this.categoryHierarchy$, this.evaluationRule$]).pipe(
      takeUntil(this.ngUnsubscribe$),
      map(([result, categoryMap, categoryHierarchy, evaluations]) => {
        this.resultDate = result?.finishedAt;

        const isCurrentFramework = (result as { isCurrentCompetencyFramework?: boolean })?.isCurrentCompetencyFramework === true;

        // Create a map of category results by categoryUuid for quick lookup
        const categoryResultsMap = new Map<string, PapCategoryResult | MngCategoryResult>(
          result?.categoryResults.map((val) => [val.categoryUuid, val] as [string, PapCategoryResult | MngCategoryResult]) || []
        );

        const superCategoryResultGroups = categoryHierarchy
          .map((hierarchy) => {
            const categoryResultGroups = hierarchy.subCategories
              .filter((subCategory) => categoryResultsMap.has(subCategory.uuid))
              .map((subCategory) => {
                const categoryResult = categoryResultsMap.get(subCategory.uuid);
                if (!categoryResult) {
                  throw new Error(`Category result not found for ${subCategory.uuid}`);
                }
                return {
                  category: categoryMap[subCategory.uuid],
                  evaluation: {
                    code: categoryResult.evaluationCode,
                    backgroundColor: categoryResult.evaluationBackgroundColor,
                    title: categoryResult.evaluationTitle,
                    textColor: categoryResult.evaluationTextColor,
                  },
                  // New framework: pictograms belong to areas only, not to competencies
                  iconUrl: isCurrentFramework
                    ? ''
                    : categoryMap[subCategory.uuid].activeIconUrl || categoryMap[subCategory.uuid].inactiveIconUrl || '',
                  score: categoryResult.score != null ? Math.round(+categoryResult.score) : null,
                  maxScore: categoryResult.maxScore != null ? Math.round(+categoryResult.maxScore) : null,
                  percent: categoryResult.percent != null ? Math.round(+categoryResult.percent) : null,
                };
              });

            // New framework: the backend evaluates areas from summed points (answers "X" lower the
            // max score), so its area row is authoritative. Old framework has no area rows - average.
            const areaResult = categoryResultsMap.get(hierarchy.superCategory.uuid);
            if (areaResult) {
              return {
                category: hierarchy.superCategory,
                iconUrl: hierarchy.superCategory.activeIconUrl || hierarchy.superCategory.inactiveIconUrl || '',
                score: areaResult.score != null ? +areaResult.score : null,
                maxScore: areaResult.maxScore != null ? +areaResult.maxScore : null,
                percent: areaResult.percent != null ? +areaResult.percent : null,
                categoryResultGroups,
              };
            }

            return {
              category: hierarchy.superCategory,
              iconUrl: hierarchy.superCategory.activeIconUrl || hierarchy.superCategory.inactiveIconUrl || '',
              score: this.calculateAverage(categoryResultGroups, 'score'),
              maxScore: this.calculateAverage(categoryResultGroups, 'maxScore'),
              percent: this.calculateAverage(categoryResultGroups, 'percent'),
              categoryResultGroups,
            };
          })
          .filter((group) => group.categoryResultGroups.length > 0);

        return superCategoryResultGroups.map((val) => {
          const percent = val.percent;
          if (percent == null) {
            return { ...val, evaluation: undefined };
          }
          if (isCurrentFramework) {
            return { ...val, evaluation: AREA_EVALUATION };
          }
          return {
            ...val,
            evaluation: evaluations?.ranges.filter((range) => +range.maxPercent >= percent && percent >= +range.minPercent)[0],
          };
        });
      })
    );
  }

  private calculateAverage(
    arr: CategoryResultGroup[],
    property: keyof Pick<CategoryResultGroup, 'score' | 'maxScore' | 'percent'>
  ): number | null {
    const values = (arr ?? []).map((v) => v[property]).filter((v): v is number => v !== null && v !== undefined);
    if (values.length === 0) {
      return null;
    }
    return values.reduce((a, b) => a + b) / values.length;
  }
}
